import { Component, inject, signal, WritableSignal, ChangeDetectionStrategy } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatIcon } from '@angular/material/icon';
import { MatRipple } from '@angular/material/core';
import { AUTH_SERVICE, AuthService } from '../../services/auth.service';
import TYPES from '../../constants/types';
import { Dialog, DialogModule } from '@angular/cdk/dialog';
import { ConfirmDialogComponent } from '../../components/confirm-dialog/confirm-dialog.component';
import { User } from '../../models/user';
import { DATABASE_SERVICE, DatabaseService } from '../../services/database.service';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { ConfigService } from '../../services/config.service';
import { Router, RouteReuseStrategy } from '@angular/router';
import { environment } from '../../../environments/environment';
import { PsychologistService } from '../../services/psychologist.service';
import { Psychologist } from '../../models/psychologists';
import { CustomRouteReuseStrategy } from '../../util/custom-route-reuse-strategy';
import { UserPsychologistService } from '../../services/user-psychologist.service';
import { UserPsychologist } from '../../models/user-psychologist';
import { LogSharedService } from '../../services/log-shared.service';
import { LogShared } from '../../models/log-shared';
import { DatePipe } from '@angular/common';
import { EdgeFunctionCallerService } from '../../services/edge-function-caller.service';
import { LoadingComponent } from '../../components/loading/loading.component';
import { MatSelectModule } from '@angular/material/select';
import { PushService } from '../../services/push.service';

@Component({
  selector: 'app-profile',
  imports: [
    TranslatePipe,
    DatePipe,
    MatIcon,
    MatRipple,
    DialogModule,
    MatSnackBarModule,
    LoadingComponent,
    MatSelectModule
],
  templateUrl: './profile.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './profile.component.scss'
})
export class ProfileComponent {

  authService: AuthService = inject(AUTH_SERVICE);
  databaseService: DatabaseService<User> = inject(DATABASE_SERVICE);
  pushService: PushService = inject(PushService);
  psychologistService: PsychologistService<Psychologist> = inject(PsychologistService);
  userPsychologistService: UserPsychologistService<UserPsychologist> = inject(UserPsychologistService);
  logSharedService: LogSharedService<LogShared> = inject(LogSharedService);
  configService: ConfigService = inject(ConfigService);
  edgeFunctionCallerService: EdgeFunctionCallerService = inject(EdgeFunctionCallerService);

  psychologist: WritableSignal<Psychologist | null> = signal<Psychologist| null>(null);
  userPsychologist: WritableSignal<UserPsychologist | null> = signal<UserPsychologist| null>(null);
  loading: WritableSignal<boolean> = signal<boolean>(false);
  loadingStripe: WritableSignal<boolean> = signal<boolean>(false);

  dialog: Dialog = inject(Dialog);
  snackBar:MatSnackBar = inject(MatSnackBar);
  translate: TranslateService = inject(TranslateService);

  PROFILES:any;

  LANGUAGES:any;
  STATUS:any;
  PLANS: any;
  STRIPE_STATUS: any;
  COUNTRY_GROUPS: any;
  COUNTRIES: any;

  constructor(
    private router: Router,
    private reuseStrategy: RouteReuseStrategy
  ){

    this.LANGUAGES = TYPES.LANGUAGES;
    this.PROFILES = TYPES.DATABASE.PROFILES;
    this.STATUS = TYPES.DATABASE.STATUS;
    this.PLANS = TYPES.DATABASE.PLANS;
    this.STRIPE_STATUS = TYPES.DATABASE.STRIPE_STATUS;
    this.COUNTRY_GROUPS = TYPES.DATABASE.COUNTRY_GROUPS;
    this.COUNTRIES = TYPES.DATABASE.COUNTRY_GROUPS[1].countries;


    if(this.authService.user()?.profile === this.PROFILES.PSYCHOLOGIST.key){
      //Delete the previous cache from historical screen
      const customReuseStrategy = this.reuseStrategy as CustomRouteReuseStrategy;
      customReuseStrategy.clearRoute('home/(home:historical)');
    }

    const userId: string | undefined = this.authService.user()?.id;
    if(userId){
      this.loading.set(true);
      this.psychologistService.getPsychologistByUserId(userId).then(
        (data) => {
          this.psychologist.set(data);
          
          this.userPsychologistService.getUserPsychologistByUserId(userId).then(
            (data) => {
              this.userPsychologist.set(data);
              this.loading.set(false);
            }
          );
        }
      );
    }
  }

  getUserProfile(){
    switch(this.authService.user()?.profile){
      case 1: 
        return this.PROFILES['USER']
      case 2:
        return this.PROFILES['PSYCHOLOGIST']
      case 3:
        return this.PROFILES['ADMIN']
      default:
        return this.PROFILES['USER']
    }
  }

  async logout(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    
    const userId = this.authService.user()?.id;
    if(userId){
      await this.pushService.removeValidSubscription(userId);
    }

    await this.authService.signOut();
  }

  deleteAccount(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'delete_account-dialog.alert_title',
        message: 'delete_account-dialog.alert_message'
      },
    });

    dialogRef.closed.subscribe(async(result:any)  => {
      if(result?.action === 'continue'){
        localStorage.removeItem('custom-body-reactions');
        localStorage.removeItem('custom-feelings');
        localStorage.removeItem('system-body-reactions');
        localStorage.removeItem('system-feelings');
        localStorage.removeItem('preferedLanguage');
        await this.authService.deleteAccount();
        this.router.navigate(['/login']);
      }
    });

  }

  view(page:string){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    if(page === 'policies'){
      this.router.navigate(['/policies']);
    }else if(page === 'terms'){
      this.router.navigate(['/terms']);
    }else if(page === 'become-psychologist'){
      this.router.navigate(['/request-psychologist']);
    }else if(page === 'request-association'){
      this.router.navigate(['/associate-to-psychologist']);
    }else if(page === 'choose-a-plan'){
      this.router.navigate(['/choose-a-plan']);
    }else if(page === 'patient-list'){
      this.router.navigate(['/patient-list']);
    }else if(page === 'invite-patient'){
      this.router.navigate(['/invite-patient']);
    }else if(page === 'invite-list'){
      this.router.navigate(['/invite-list']);
    }else if(page === 'stripe-sub-area'){
      this.loadingStripe.set(true);
      this.edgeFunctionCallerService.manageSubscription();
    }else if(page === 'account'){
      this.router.navigate(['/account']);
    }else if(page === 'appointments-report'){
      this.router.navigate(['/appointments-report']);
    }else if(page === 'psychologist-profile'){
      this.router.navigate(['/request-psychologist'], {queryParams: {edit: true}});
    }
  }

  getVersion(){
    return environment.VERSION;
  }

  removeAssociation(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'remove-association-dialog.alert_title',
        message: 'remove-association-dialog.alert_message'
      },
    });

    dialogRef.closed.subscribe(async(result:any)  => {
      if(result?.action === 'continue'){
        const userPsychologistId = this.userPsychologist()?.id;
        if(userPsychologistId){
          await this.userPsychologistService.delete(TYPES.DATABASE.TABLES.USERS_PSYCHOLOGISTS, userPsychologistId);
          
          const userId = this.userPsychologist()?.user_id;
          const psychologistId = this.userPsychologist()?.psychologist_id;
          if(userId && psychologistId){
            await this.logSharedService.deleteByUserIdAndPsychologistId(userId, psychologistId);
            
            //Delete the previous cache from historical screen
            const customReuseStrategy = this.reuseStrategy as CustomRouteReuseStrategy;
            customReuseStrategy.clearRoute('home/(home:historical)');          
          }
        }

        this.userPsychologist.set(null);

        const message:string = this.translate.instant('info.association_finalized');
        const action:string = this.translate.instant('buttons.dismiss');
        this.snackBar.open(message, action);
      }
    });
  }

  async restoreSubscription(){
    if ('vibrate' in navigator) {
      navigator.vibrate(100);
    }

    const userEmail = this.authService.user()?.email;
    const userId = this.authService.user()?.id;
    
    if(userEmail && userId){
      this.loadingStripe.set(true);
      const data = await this.edgeFunctionCallerService.reconcileSubscription(userEmail, userId);
      if(data.status === 'reconciled'){
        this.router.navigate(['/info/sub_done/info.sub_restored/info.premium_access/info.thank_you/buttons.continue'], {replaceUrl: true});
      }else if(data.status === 'no_active_subscription'){
        const message:string = this.translate.instant('info.sub_not_found');
        const action:string = this.translate.instant('buttons.dismiss');
        this.snackBar.open(message, action);
        this.router.navigate(['/home'], {replaceUrl: true});
      }else {
        const message:string = this.translate.instant('info.unkonwn_error', {error: data?.error || ''});
        const action:string = this.translate.instant('buttons.dismiss');
        this.snackBar.open(message, action);
      }
    }
  }

  getLanguage(){
    return this.LANGUAGES.find((item:any)=>{return item.locale === this.configService.preferedLanguage()});
  }

  getCountryName(countryCode: string){
    return this.translate.instant(TYPES.APPLICATION.COUNTRIES.find((item:any)=>{return item.code === countryCode})?.name || '');
  }

  getSessionLanguage(){
    let languages = '';
    for(const language of this.LANGUAGES){
      if(this.psychologist()?.session_languages?.includes(language.code)){
        languages += language.name+', '
      }
    }
    return languages.slice(0, languages.length-2);
  }

}
